import { KEYCLOAK_CONFIG, KEYCLOAK_ENDPOINTS, REDIRECT_URI, POST_LOGOUT_REDIRECT_URI } from '../config/keycloak'

const PKCE_CODE_VERIFIER_KEY = 'kc_pkce_code_verifier'
const OAUTH_STATE_KEY = 'kc_oauth_state'
const LOGOUT_INTENT_KEY = 'kc_logout_intent'

const cryptoApi = typeof window !== 'undefined' ? window.crypto || window.msCrypto : null

const randomString = (length = 32) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

  if (cryptoApi?.getRandomValues) {
    const randomValues = new Uint8Array(length)
    cryptoApi.getRandomValues(randomValues)
    return Array.from(randomValues, (value) => charset[value % charset.length]).join('')
  }

  // Fallback for non-secure contexts (development http://localhost)
  return Array.from({ length }, () => charset[Math.floor(Math.random() * charset.length)]).join('')
}

const base64UrlEncode = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ''
  bytes.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

class KeycloakAuth {
  async login(forceLogin = false) {
    const codeVerifier = randomString(96)
    const state = randomString(24)

    sessionStorage.setItem(PKCE_CODE_VERIFIER_KEY, codeVerifier)
    sessionStorage.setItem(OAUTH_STATE_KEY, state)

    let codeChallenge = codeVerifier
    let method = 'plain'

    if (cryptoApi?.subtle) {
      const encoder = new TextEncoder()
      const encoded = encoder.encode(codeVerifier)
      const digest = await cryptoApi.subtle.digest('SHA-256', encoded)
      codeChallenge = base64UrlEncode(digest)
      method = 'S256'
    }

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CONFIG.clientId,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: REDIRECT_URI,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: method,
    })

    if (forceLogin) {
      params.set('prompt', 'login')
    }

    window.location.assign(`${KEYCLOAK_ENDPOINTS.authorization}?${params.toString()}`)
  }

  async handleCallback(code, state) {
    const savedState = sessionStorage.getItem(OAUTH_STATE_KEY)
    if (!savedState || savedState !== state) {
      throw new Error('Invalid OAuth state')
    }

    const codeVerifier = sessionStorage.getItem(PKCE_CODE_VERIFIER_KEY)
    if (!codeVerifier) {
      throw new Error('Missing PKCE code verifier')
    }

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: KEYCLOAK_CONFIG.clientId,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: codeVerifier,
    })

    const response = await fetch(KEYCLOAK_ENDPOINTS.token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error_description || 'Failed to exchange authorization code')
    }

    sessionStorage.removeItem(PKCE_CODE_VERIFIER_KEY)
    sessionStorage.removeItem(OAUTH_STATE_KEY)
    return response.json()
  }

  logout(idToken) {
    sessionStorage.setItem(LOGOUT_INTENT_KEY, 'true')
    const params = new URLSearchParams({
      client_id: KEYCLOAK_CONFIG.clientId,
      post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
    })
    if (idToken) {
      params.set('id_token_hint', idToken)
    }

    const logoutUrl = `${KEYCLOAK_ENDPOINTS.logout}?${params.toString()}`

    window.location.assign(logoutUrl)
  }

  shouldForceReauth() {
    return sessionStorage.getItem(LOGOUT_INTENT_KEY) === 'true'
  }

  clearLogoutIntent() {
    sessionStorage.removeItem(LOGOUT_INTENT_KEY)
  }
}

export default new KeycloakAuth()
